/**
 * Referral attribution (D11, D17, D20).
 *
 * When this visit lands with `?referrer=<brand>` after a cross-brand redirect,
 * the brand is kept in sessionStorage for this visit only: no 30-day window and
 * no fallback to the current host. `createBookingIntent` sends it as
 * `input.referrer`; the server derives `referringBrandSite` and ignores
 * anything that is not another brand. It is cleared once a payment succeeds, so
 * only the first paid booking after the redirect records the fee; a failed
 * payment can be retried and still records it.
 *
 * Mirror of Flutter's `referral_attribution.dart`.
 */

const STORAGE_KEY = 'referralBrand';

/** Normalise a referrer marker the same way the backend does (bare host, lowercase, port kept). */
export function normalizeReferralMarker(raw: string): string {
  const trimmed = raw.trim();
  try {
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withScheme);
    return url.host.replace(/^www\./, '').toLowerCase();
  } catch {
    return trimmed
      .toLowerCase()
      .replace(/^www\./, '')
      .replace(/\/$/, '');
  }
}

/**
 * Call once on app startup. If the current URL carries `?referrer=<brand>`,
 * remember it for this visit (overwriting any earlier value).
 */
export function captureReferralFromUrl(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const raw = params.get('referrer');
  if (!raw) return;

  const marker = normalizeReferralMarker(raw);
  if (!marker) return;

  try {
    sessionStorage.setItem(STORAGE_KEY, marker);
  } catch {
    // sessionStorage unavailable — silent
  }
}

/** The brand this visit was redirected from, or null. */
export function getStoredReferralMarker(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * The `referrer` to send with createBookingIntent: only a brand this visit was
 * redirected from. There is no fallback to the current host (D17).
 */
export function getReferralMarkerForBooking(): string | null {
  return getStoredReferralMarker();
}

/** Clear the stored referral marker after payment succeeds (D20). */
export function clearReferralMarker(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
